import { Icon } from "@iconify/react";
import { formatDate } from "../../utils/formatters";
import Button from "../common/Button";
import { toast } from "react-toastify";

const ScanHistory = ({ scanHistory = [], onViewReport, isLoading = false }) => {
  const getScoreColor = (score) => {
    if (score >= 90) return "text-green-600 bg-green-50 border-green-200";
    if (score >= 70) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon icon="mdi:history" className="text-gray-600 text-xl" />
            <h2 className="text-lg font-semibold text-gray-800">
              Recent Scans
            </h2>
          </div>
          <span className="text-sm text-gray-500">
            {scanHistory.length} scan{scanHistory.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="divide-y divide-gray-200">
        {isLoading ? (
          // Loading skeleton
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg animate-pulse"
              >
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-16 bg-gray-200 rounded"></div>
                  <div className="h-9 w-24 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : scanHistory.length > 0 ? (
          scanHistory.map((scan) => {
            const scanId = scan._id || scan.id || scan.scanResultId;
            const score =
              scan.summary?.complianceScore ??
              (scan.summary?.totalViolations === 0
                ? 100
                : Math.max(
                    0,
                    100 -
                      (scan.summary?.totalViolations ||
                        scan.violationCount ||
                        scan.violations?.length ||
                        0) *
                        5,
                  ));
            const violations =
              scan.summary?.totalViolations ?? scan.violations?.length ?? 0;
            const totalIssues = scan.summary?.totalIssues ?? null;
            const passes =
              scan.summary?.totalPasses ?? scan.passes?.length ?? 0;

            return (
              <div
                key={scanId}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 hover:bg-gray-50 transition-colors"
              >
                {/* Left side - URL and details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                        violations > 0
                          ? "bg-red-100 text-red-600"
                          : "bg-green-100 text-green-600"
                      }`}
                    >
                      <Icon
                        icon={
                          violations > 0
                            ? "mdi:alert-circle"
                            : "mdi:check-circle"
                        }
                        className="text-xl"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm text-gray-800 truncate">
                        {scan.url}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {scan.wcagLevel?.toUpperCase().replace("WCAG", "WCAG ")}{" "}
                        &bull; {formatDate(scan.createdAt || scan.updatedAt)}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {totalIssues !== null && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-orange-50 rounded text-orange-600">
                            <Icon
                              icon="mdi:alert-circle-outline"
                              className="text-orange-500 text-sm"
                            />
                            {totalIssues} total issues
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 rounded text-gray-600">
                          <Icon
                            icon="mdi:alert"
                            className="text-red-500 text-sm"
                          />
                          {violations} violations
                        </span>

                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 rounded text-gray-600">
                          <Icon
                            icon="mdi:check"
                            className="text-green-500 text-sm"
                          />
                          {passes} passes
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right side - Score and action */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div
                    className={`px-3 py-1 rounded-lg border text-center flex items-center gap-4 ${getScoreColor(score)}`}
                  >
                    <div className="text-lg font-bold">{score}%</div>
                    <div className="text-xs font-medium uppercase">Score</div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      if (onViewReport) {
                        onViewReport(scan);
                      } else {
                        toast.info(`Viewing report for ${scan.url}`);
                      }
                    }}
                    className="flex items-center gap-1.5"
                  >
                    <Icon icon="mdi:eye" className="text-base" />
                    <span>View</span>
                  </Button>
                </div>
              </div>
            );
          })
        ) : (
          // Empty state
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Icon icon="mdi:file-search" className="text-3xl text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-800 mb-1">
              No scan history yet
            </h3>
            <p className="text-xs text-gray-500">
              Run your first scan to see results here
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanHistory;
